import { useEffect, useState } from 'react';
// api client used for typed calls if needed in future; inventory uses fetch directly for now
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface ItemInstance {
  instance_id: string;
  definition_id: string;
  quantity: number;
  properties: { strings: Record<string, string>; numbers: Record<string, number> };
}

interface BagView {
  scope: unknown;
  state: { items: Record<string, ItemInstance>; equipment: Record<string, string> };
  slot_count: number;
  total_items: number;
}

export default function Inventory({ session }: Props) {
  const playerId = session.playerId;
  const [bag, setBag] = useState<BagView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [defId, setDefId] = useState('iron_ore');
  const [qty, setQty] = useState(5);
  const [busy, setBusy] = useState(false);

  const reload = () => {
    fetch(`/api/v1/games/${session.gameId}/modules/inventory/bag/player/${playerId}`)
      .then(r => r.json())
      .then(setBag)
      .catch(e => setError(e.message));
  };
  useEffect(reload, [session.gameId, playerId]);

  const give = async () => {
    setBusy(true); setError(null);
    try {
      await fetch(`/api/v1/games/${session.gameId}/modules/inventory/bag/player/${playerId}/give`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ definition_id: defId, quantity: qty }),
      });
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (instanceId: string, count: number) => {
    setBusy(true); setError(null);
    try {
      await fetch(`/api/v1/games/${session.gameId}/modules/inventory/bag/player/${playerId}/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instance_id: instanceId, quantity: count }),
      });
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const equip = async (instanceId: string) => {
    setBusy(true); setError(null);
    try {
      await fetch(`/api/v1/games/${session.gameId}/modules/inventory/bag/player/${playerId}/equip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instance_id: instanceId }),
      });
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const unequip = async (slot: string) => {
    setBusy(true); setError(null);
    try {
      await fetch(`/api/v1/games/${session.gameId}/modules/inventory/bag/player/${playerId}/unequip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot }),
      });
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const items = bag ? Object.values(bag.state.items) : [];
  const equipment = bag ? Object.entries(bag.state.equipment) : [];
  const shortPlayer = `${playerId.slice(0, 8)}…${playerId.slice(-4)}`;

  return (
    <Container>
      <PageHeader
        title="Inventory"
        subtitle="Browse selected player items, give/remove, equip/unequip."
      />

      {error && <div className="error-banner">{error}</div>}

      <div className="card mb-5 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-1">
            Active player
          </div>
          <code className="text-fg" title={playerId}>{shortPlayer}</code>
        </div>
        <div className="flex gap-6 text-right">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-1">
              Slots used
            </div>
            <div className="text-2xl font-semibold tabular-nums">{bag?.slot_count ?? 0}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-fg-dim mb-1">
              Total items
            </div>
            <div className="text-2xl font-semibold tabular-nums">{bag?.total_items ?? 0}</div>
          </div>
        </div>
      </div>

      {equipment.length > 0 && (
        <>
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">
            Equipment
          </h2>
          <div className="table-wrap mb-5">
            <table>
              <thead>
                <tr><th>Slot</th><th>Item</th><th></th></tr>
              </thead>
              <tbody>
                {equipment.map(([slot, iid]) => {
                  const item = bag?.state.items[iid];
                  return (
                    <tr key={slot}>
                      <td className="font-medium">{slot}</td>
                      <td>{item?.definition_id ?? iid}</td>
                      <td><button className="btn" disabled={busy} onClick={() => unequip(slot)}>Unequip</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">
        Bag
      </h2>
      {items.length === 0 ? (
        <div className="empty mb-5">
          <div className="text-fg-2 font-medium mb-1">Empty inventory</div>
          <div className="text-xs text-fg-muted">
            Define items in Config Explorer, publish, then give below.
          </div>
        </div>
      ) : (
        <div className="table-wrap mb-5">
          <table>
            <thead>
              <tr><th>Item</th><th>Qty</th><th>Instance ID</th><th></th></tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.instance_id}>
                  <td className="font-medium">{item.definition_id}</td>
                  <td className="tabular-nums">{item.quantity}</td>
                  <td><code className="text-[11px] text-fg-muted">{item.instance_id}</code></td>
                  <td className="flex gap-2">
                    <button className="btn" disabled={busy} onClick={() => equip(item.instance_id)}>Equip</button>
                    <button className="btn btn-danger" disabled={busy} onClick={() => remove(item.instance_id, 1)}>-1</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-3">
        Give item
      </h2>
      <div className="card">
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">
              Definition ID
            </label>
            <input className="input" value={defId} onChange={e => setDefId(e.target.value)} />
          </div>
          <div>
            <label className="block text-[11.5px] uppercase tracking-wider font-medium text-fg-dim mb-1.5">
              Qty
            </label>
            <input
              className="input"
              type="number"
              value={qty}
              onChange={e => setQty(parseInt(e.target.value, 10) || 1)}
              style={{ width: 100 }}
            />
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={give}>Give</button>
        </div>
        <div className="text-xs text-fg-muted mt-3">
          Item definitions must be created in <strong>Config Explorer</strong> &amp; published first.
        </div>
      </div>
    </Container>
  );
}
